/**
 * PlayerRoot — routes activation gate.
 * If not activated, send to /player/activate.
 * If activated, render the protected outlet (which mounts PlayerLayout).
 */
import React, { useEffect } from "react";
import { Navigate, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useDevice } from "./lib/DeviceContext";
import { getActiveProfileId } from "./lib/profiles";
import { Loader2 } from "lucide-react";
import PlayerErrorBoundary from "./components/PlayerErrorBoundary";
import { scheduleBootUpdateCheck } from "./lib/bootUpdateCheck";
import "./vu-theme.css";

/**
 * The built-in player (HLS.js → mpv fallback) is the enforced default. Any
 * older install that still has VLC / mpv / PotPlayer / custom selected gets
 * reset to "builtin" exactly once. After that the user can re-pick an
 * external player from Settings if they want.
 */
function enforceBuiltinDefault() {
  if (localStorage.getItem("vupp_builtin_default_v1") === "yes") return;
  localStorage.setItem("vupp_external_player", "builtin");
  localStorage.setItem("vupp_builtin_default_v1", "yes");
}

/**
 * v1.3.34 — clear the active profile on every cold app boot so the
 * "Who's watching?" picker is always the first thing the user sees
 * after activation, matching the spec admin asked for. We use a
 * session-scoped sentinel so React-only re-renders within a single
 * session don't bounce the user back to the picker mid-use.
 */
function maybeClearActiveProfileOnBoot() {
  try {
    if (sessionStorage.getItem("vupp_session_started") === "1") return;
    sessionStorage.setItem("vupp_session_started", "1");
    localStorage.removeItem("vupp_active_profile");
  } catch {/* noop */}
}

export default function PlayerRoot() {
  const { isActivated, loading } = useDevice();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    enforceBuiltinDefault();
    maybeClearActiveProfileOnBoot();
    return scheduleBootUpdateCheck(navigate);
  }, [navigate]);

  if (loading) {
    return (
      <div className="vu-splash-bg min-h-screen flex flex-col items-center justify-center gap-6 text-white/60">
        <img
          src="/za-logo-512.png"
          alt="ZA Player Pro"
          className="w-40 h-40 object-contain drop-shadow-[0_0_30px_rgba(245,2,97,0.35)]"
        />
        <Loader2 className="w-8 h-8 animate-spin" style={{ color: "#f50261" }} />
      </div>
    );
  }

  const path = location.pathname;
  const isActivationPage = path === "/player/activate";
  const isProfilesPage = path === "/player/profiles";
  const isIntroPage = path === "/player/intro";

  // Step 1: must be activated
  if (!isActivated && !isActivationPage) {
    return <Navigate to="/player/activate" replace />;
  }
  if (isActivated && isActivationPage) {
    return <Navigate to="/player/profiles" replace />;
  }

  // Step 2: must pick a profile (skip the gate for the activation/intro/profiles pages themselves)
  const hasProfile = !!getActiveProfileId();
  if (isActivated && !hasProfile && !isProfilesPage && !isIntroPage && !isActivationPage) {
    return <Navigate to="/player/profiles" replace />;
  }

  return (
    <PlayerErrorBoundary>
      <Outlet />
    </PlayerErrorBoundary>
  );
}
